import { FlightItinerary } from '../../../../redux/states/flights/getAllFlightItineraries/getAllFlightItineraries.types'
import { CountryAndCities } from '../../../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.types'
import { FulfillmentOrder } from '../../../../redux/states/offerRequest/getPublicOffers/getPublicOffers.types'
import date, { isLocalCalendarOnOrAfterToday } from '../../../../shared/functions/Date/date.functions'
import status from '../../../../shared/functions/Status/status.dictionaries'
import { AcceptFulfillmentOrderValidationResult, RequiredCountries } from './FulfillmentOrderCard.types'

export const validateFulfillmentOrderAcceptance = (
  flightItineraries: FlightItinerary[],
  order: FulfillmentOrder,
  countriesAndCities: CountryAndCities[] = []
): AcceptFulfillmentOrderValidationResult => {
  const matchingFlightItineraries: FlightItinerary[] = []

  // Use airportIataCode directly if available, otherwise fallback to parsing airportLocation
  let pickupAirportIata = order.airportIataCode || ''
  let pickupCountry = ''
  let pickupCity = ''

  // If no airportIataCode, try to parse from airportLocation (backward compatibility)
  if (!pickupAirportIata && order.airportLocation) {
    const airportLocationParts = order.airportLocation.split(',').map((part) => part.trim())

    // Check if first part is a 3-letter IATA code (all uppercase letters)
    if (airportLocationParts[0] && /^[A-Z]{3}$/.test(airportLocationParts[0])) {
      pickupAirportIata = airportLocationParts[0]
      pickupCity = airportLocationParts[1] || ''
      pickupCountry = airportLocationParts[2] || airportLocationParts[airportLocationParts.length - 1] || ''
    } else {
      // No IATA code, try to parse as "City, Country"
      pickupCity = airportLocationParts[0] || ''
      pickupCountry = airportLocationParts[airportLocationParts.length - 1] || ''
    }
  }

  // Filter flight itineraries to only include valid ones
  const validFlightItineraries = flightItineraries.filter((flightItinerary) => {
    const firstLeg = flightItinerary.itinerary.legs[0]
    const travelDate = firstLeg?.travelDate
    const isExternal = flightItinerary.itinerary.itinerarySource === 'external'
    const isTravelOnOrAfterToday = isLocalCalendarOnOrAfterToday(travelDate)

    if (isExternal) {
      return (
        isTravelOnOrAfterToday &&
        flightItinerary.status.name !== status.externalFlightBookingStatus.externalFlightBookingDetailsConfirmed
      )
    } else {
      return isTravelOnOrAfterToday && flightItinerary.status.name !== status.flightStatus.flightBooked
    }
  })

  for (const flightItinerary of validFlightItineraries) {
    const legs = flightItinerary.itinerary.legs
    let foundPickupLocation = false
    let foundDeliveryLocation = false
    let correctDirection = false
    let arrivalDateValid = true

    // Track the sequence of locations visited
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i]
      const originAirportIata = leg.origin.airport.iataCode
      const originCountryCode = leg.origin.country.iataCode
      const originCountryName = leg.origin.country.name
      const originCityName = leg.origin.city.name

      const destinationCountryCode = leg.destination.country.iataCode
      const destinationCountryName = leg.destination.country.name

      // Check if this leg starts from pickup location (airport)
      // Match by IATA code first (most reliable), then by country/city
      if (pickupAirportIata) {
        if (originAirportIata === pickupAirportIata) {
          foundPickupLocation = true
        }
      } else {
        // Fallback to country/city matching if no IATA code
        if (
          originCountryCode === order.destinationCountry ||
          originCountryName === pickupCountry ||
          originCityName === pickupCity
        ) {
          foundPickupLocation = true
        }
      }

      // Check if this leg arrives at delivery location
      // Match by IATA country code OR full country name (country-level match only, no city check)
      const orderCountryCode =
        order.destinationCountryCode ||
        (order.destinationCountry?.length === 2 ? order.destinationCountry.toUpperCase() : '')
      const orderCountryLower = order.destinationCountry?.toLowerCase() ?? ''
      const destinationNameLower = destinationCountryName?.toLowerCase() ?? ''
      const isIataMatch = orderCountryCode && destinationCountryCode === orderCountryCode
      const isNameMatch = orderCountryLower && destinationNameLower === orderCountryLower
      const isResolvedIataMatch =
        !orderCountryCode &&
        orderCountryLower &&
        countriesAndCities.length > 0 &&
        countriesAndCities.some(
          (country) =>
            country.countryName?.toLowerCase() === orderCountryLower &&
            country.iataCountryCode === destinationCountryCode
        )
      const deliveryCountryMatches = isIataMatch || isNameMatch || isResolvedIataMatch

      if (deliveryCountryMatches) {
        foundDeliveryLocation = true

        // Check arrival date only if neededByDate exists
        if (order.neededByDate && leg.travelDate) {
          const travelDateStr = date(leg.travelDate).format('yyyy-MM-dd')
          const neededByDateStr = date(order.neededByDate).format('yyyy-MM-dd')

          if (travelDateStr && neededByDateStr && travelDateStr > neededByDateStr) {
            arrivalDateValid = false
          }
        }

        // Check if we found pickup location before finding delivery location
        if (foundPickupLocation) {
          correctDirection = true
        }
      }
    }

    const isMatch = foundPickupLocation && foundDeliveryLocation && correctDirection && arrivalDateValid

    if (isMatch) {
      matchingFlightItineraries.push(flightItinerary)
    }
  }

  // Look up full names from countries/cities data
  let pickupAirportName = ''
  let pickupCityName = ''
  let pickupCountryName = ''
  let pickupCountryIataCode = ''
  let deliveryCountryName = ''

  // Look up airport, city, and country from airport IATA code
  if (pickupAirportIata && countriesAndCities.length > 0) {
    for (const country of countriesAndCities) {
      for (const city of country.cities) {
        const airport = city.airports.find((airport) => airport.iataCode === pickupAirportIata)
        if (airport) {
          pickupAirportName = airport.airportName
          pickupCityName = city.cityName
          pickupCountryName = country.countryName
          pickupCountryIataCode = country.iataCountryCode
          break
        }
      }
      if (pickupAirportName) break
    }
  }

  // Look up city and country from destination
  // The backend may return IATA codes or full names, so we need to handle both
  if (order.destinationCity && order.destinationCountry && countriesAndCities.length > 0) {
    // First, try to find by IATA codes (if they are IATA codes)
    let foundByIata = false
    for (const country of countriesAndCities) {
      // Try city IATA code match
      const city = country.cities.find((city) => city.iataCityCode === order.destinationCity)
      if (city && country.iataCountryCode === order.destinationCountry) {
        deliveryCountryName = country.countryName
        foundByIata = true
        break
      }
    }

    // If not found by IATA codes, try to find by country IATA code only
    if (!foundByIata) {
      const country = countriesAndCities.find((country) => country.iataCountryCode === order.destinationCountry)
      if (country) {
        deliveryCountryName = country.countryName
        // Try to find city by IATA code within this country
        const city = country.cities.find((city) => city.iataCityCode === order.destinationCity)
        if (city) {
          foundByIata = true
        }
      }
    }

    // If still not found by IATA codes, try name matching (in case backend returns full names)
    if (!foundByIata) {
      for (const country of countriesAndCities) {
        // Try exact country name match
        if (country.countryName.toLowerCase() === order.destinationCountry.toLowerCase()) {
          deliveryCountryName = country.countryName
          // Try to find city by name match
          const city = country.cities.find(
            (city) => city.cityName.toLowerCase() === order.destinationCity.toLowerCase()
          )
          if (city) {
            break
          }
        }
      }
    }
  }

  // Build display name for pickup location
  // Format: "Airport Name, City, Country" (e.g., "Norman Manley International Airport, Kingston, Jamaica")
  // Fallback to simpler formats if airport name not available
  let pickupDisplayName = ''
  if (pickupAirportName && pickupCityName && pickupCountryName) {
    pickupDisplayName = `${pickupAirportName}, ${pickupCityName}, ${pickupCountryName}`
  } else if (pickupAirportName && pickupCityName) {
    pickupDisplayName = `${pickupAirportName}, ${pickupCityName}`
  } else if (pickupCityName && pickupCountryName) {
    pickupDisplayName = `${pickupCityName}, ${pickupCountryName}`
  } else if (pickupAirportName) {
    pickupDisplayName = pickupAirportName
  } else if (order.airportLocation) {
    // Fallback: use location description if available, but try to clean it up
    const locationParts = order.airportLocation.split(',').map((part) => part.trim())
    // If it starts with IATA code, skip it and use the rest
    if (locationParts[0] && /^[A-Z]{3}$/.test(locationParts[0])) {
      pickupDisplayName = locationParts.slice(1).join(', ') || locationParts[0]
    } else {
      pickupDisplayName = order.airportLocation
    }
  } else {
    pickupDisplayName = pickupCity || pickupCountry || order.airportIataCode || 'Unknown'
  }

  // Use backend values as fallback if lookup didn't find matches
  // (Backend might already return full names in some cases)
  const finalDeliveryCountryName = deliveryCountryName || order.destinationCountry

  // Resolve delivery country IATA code for search (FlightSearchWidget expects IATA, not name)
  const deliveryCountryIataCode =
    order.destinationCountryCode ||
    (countriesAndCities.length > 0 &&
      countriesAndCities.find(
        (country) => country.countryName?.toLowerCase() === order.destinationCountry?.toLowerCase()
      )?.iataCountryCode) ||
    order.destinationCountry

  // Build required countries object for display
  // Note: itemLocation.iataCode must be the COUNTRY IATA code (not airport code) to match FlightSearchWidget logic
  // For fulfillment orders: match by destination country only (not city) - travelers can deliver to any city in the country
  const requiredCountries: RequiredCountries = {
    itemLocation: {
      iataCode: pickupCountryIataCode || order.destinationCountryCode || '', // Use country code, not airport code
      name: pickupDisplayName
    },
    deliveryLocation: {
      iataCode: deliveryCountryIataCode,
      cityIataCode: '', // Fulfillment orders match by country only; FlightSearchWidget will use iataCode
      name: finalDeliveryCountryName,
      city: '', // Fulfillment orders: no city restriction; show country-only in UI
      neededByDate: order.neededByDate || null
    }
  }

  if (matchingFlightItineraries.length === 0) {
    return {
      multipleOptions: false,
      noMatchingFlightItineraries: true,
      requiredCountries,
      flightItineraries: []
    }
  } else if (matchingFlightItineraries.length > 1) {
    return {
      multipleOptions: true,
      noMatchingFlightItineraries: false,
      flightItineraries: matchingFlightItineraries,
      requiredCountries
    }
  } else {
    return {
      multipleOptions: false,
      noMatchingFlightItineraries: false,
      flightItineraries: matchingFlightItineraries,
      requiredCountries
    }
  }
}
